// crewbit-admin/src/components/money/AuthoringReviewSection.tsx
//
// Spec: §InvoiceAuthoring.7.2 Step 3 (admin parallel). Section card with
// issue date + terms picker (inline pill row 0/7/14/30/60/Custom per §3g)
// + custom-date picker + notes textarea.
//
// Mirrors crewbit-mobile/components/money/authoring/Step3ReviewSend.tsx +
// TermsPicker.tsx — admin folds the pill row into this file (no separate
// component file per §3g).

import { useState } from 'react';
import { DatePicker } from '../common/DatePicker';
import { palette, radii, spacing, typography } from '../../styles/tokens';
import {
  resolveTaxRatePercent,
  resolveDepositPercentage,
  resolveLateFeeDisplay,
  type AuthoringDraftState,
} from '../../hooks/useInvoiceDraft';
import OverridePill from './clients/OverridePill';
import OverridePillTooltip from './clients/OverridePillTooltip';
import ClientDefaultsRow from './clients/ClientDefaultsRow';
import PolymorphicLateFeeEditor from './clients/PolymorphicLateFeeEditor';
import type { ClientDefaultsSources, LateFeePolicy } from '../../types/clientDefaults';

interface Props {
  state: AuthoringDraftState;
  onChange: (patch: Partial<AuthoringDraftState>) => void;
}

const PRESETS: { days: number | 'custom'; label: string }[] = [
  { days: 0, label: 'Due on receipt' },
  { days: 7, label: 'Net 7' },
  { days: 14, label: 'Net 14' },
  { days: 30, label: 'Net 30' },
  { days: 60, label: 'Net 60' },
  { days: 'custom', label: 'Custom' },
];

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function diffDays(issue: string, due: string): number | null {
  const ms = new Date(`${due}T00:00:00`).getTime() - new Date(`${issue}T00:00:00`).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.round(ms / 86400000);
}

export function AuthoringReviewSection({ state, onChange }: Props) {
  const currentDays = state.dueDate ? diffDays(state.issueDate, state.dueDate) : null;
  const matchedPreset = PRESETS.find((p) => p.days === currentDays);
  const noPresetMatch = !matchedPreset || matchedPreset.days === 'custom';
  const [customSelected, setCustomSelected] = useState(false);
  const showCustomPicker = customSelected || (noPresetMatch && state.dueDate !== null);
  const [tooltipFor, setTooltipFor] = useState<keyof ClientDefaultsSources | 'tax_rate' | null>(null);

  const cd = state.clientDefaults;
  const hasClientDefaults = cd.resolved !== null && cd.sources !== null;
  const resolved = cd.resolved;
  const sources = cd.sources;
  const reasoning = cd.reasoning;
  const clientNameLabel = state.clientName;
  const taxRatePercent = resolveTaxRatePercent(state);
  const depositPercent = resolveDepositPercentage(state);
  const lateFee = resolveLateFeeDisplay(state);

  // Phase D v2 — every override-bearing row's pill source flips to
  // 'invoice_override' (amber) when the operator has typed a value.
  // Decision §D-v2.D6 (admin parallel of mobile).
  const overrideTooltipCopy = 'Custom for this invoice';
  const taxRateSource = state.taxRateOverride !== null
    ? 'invoice_override' as const
    : (sources?.tax_rate_resolved ?? null);
  const paymentTermsSource = state.paymentTermsDaysOverride !== null
    ? 'invoice_override' as const
    : (sources?.payment_terms_days ?? null);
  const depositSource = state.depositPercentageOverride !== null
    ? 'invoice_override' as const
    : (sources?.deposit_percentage ?? null);
  const lateFeeSource =
    (state.lateFeePolicyOverride !== null && state.lateFeeAmountOverride !== null)
      ? 'invoice_override' as const
      : (sources?.late_fee_policy ?? null);

  const taxRateReasoning = state.taxRateOverride !== null
    ? overrideTooltipCopy
    : (reasoning?.tax_rate_resolved ?? '');

  const lookupReasoning = (key: keyof ClientDefaultsSources | 'tax_rate'): string => {
    if (key === 'tax_rate') return taxRateReasoning;
    if (key === 'payment_terms_days' && state.paymentTermsDaysOverride !== null) {
      return overrideTooltipCopy;
    }
    if (key === 'deposit_percentage' && state.depositPercentageOverride !== null) {
      return overrideTooltipCopy;
    }
    if (
      (key === 'late_fee_policy' || key === 'late_fee_amount') &&
      state.lateFeePolicyOverride !== null && state.lateFeeAmountOverride !== null
    ) {
      return overrideTooltipCopy;
    }
    return reasoning?.[key] ?? '';
  };

  const onTaxRateChange = (raw: string) => {
    const cleaned = raw.replace(/[^\d.]/g, '');
    if (cleaned === '') {
      onChange({ taxRateOverride: null });
      return;
    }
    const num = Number(cleaned);
    if (Number.isNaN(num)) return;
    onChange({ taxRateOverride: Math.max(0, Math.min(100, num)) });
  };

  const onDepositChange = (raw: string) => {
    const cleaned = raw.replace(/[^\d.]/g, '');
    if (cleaned === '') {
      onChange({ depositPercentageOverride: null });
      return;
    }
    const num = Number(cleaned);
    if (Number.isNaN(num)) return;
    onChange({ depositPercentageOverride: Math.max(0, Math.min(100, num)) });
  };

  const onLateFeeChange = (next: { policy: LateFeePolicy | null; amount: number | null }) => {
    onChange({
      lateFeePolicyOverride: next.policy,
      lateFeeAmountOverride: next.amount,
    });
  };

  const onResetTermsOverride = () => {
    const days = resolved?.payment_terms_days ?? 14;
    onChange({
      paymentTermsDaysOverride: null,
      dueDate: addDays(state.issueDate, days),
    });
  };
  const onResetDepositOverride = () => onChange({ depositPercentageOverride: null });
  const onResetLateFeeOverride = () =>
    onChange({ lateFeePolicyOverride: null, lateFeeAmountOverride: null });

  // Phase D v2 §D-v2.D2 — preset path emits paymentTermsDaysOverride: days;
  // Custom path emits null ("operator chose date directly"). Mirrors mobile
  // TermsPicker.tsx but inlined here per admin's no-separate-component idiom.
  const onSelectPreset = (days: number | 'custom') => {
    if (days === 'custom') {
      setCustomSelected(true);
      return;
    }
    setCustomSelected(false);
    onChange({
      dueDate: addDays(state.issueDate, days),
      paymentTermsDaysOverride: days,
    });
  };

  return (
    <section style={CARD}>
      <div style={HEADER_ROW}>
        <div>
          <div style={STEP_LABEL}>Step 3 of 3</div>
          <div style={SECTION_TITLE}>Review</div>
        </div>
      </div>

      <div style={FIELD_LABEL}>Issue date</div>
      <DatePicker
        value={state.issueDate}
        onChange={(v) => {
          if (v) onChange({ issueDate: v });
        }}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: spacing.md,
          gap: spacing.sm,
        }}
      >
        <div style={{ ...FIELD_LABEL, marginBottom: 0 }}>Payment terms</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          {paymentTermsSource && (
            <OverridePill
              source={paymentTermsSource}
              clientName={clientNameLabel}
              onClick={() => setTooltipFor('payment_terms_days')}
            />
          )}
          {state.paymentTermsDaysOverride !== null && (
            <button type="button" onClick={onResetTermsOverride} style={RESET_LINK}>
              Reset to default
            </button>
          )}
        </div>
      </div>
      <div style={PILL_ROW}>
        {PRESETS.map((p) => {
          const active =
            p.days === 'custom'
              ? showCustomPicker
              : !showCustomPicker && currentDays === p.days;
          return (
            <button
              key={String(p.days)}
              type="button"
              onClick={() => onSelectPreset(p.days)}
              style={{
                ...PILL,
                background: active ? palette.accentWash : palette.g100,
                borderColor: active ? palette.accent : 'transparent',
                color: active ? palette.accent : palette.g700,
                fontWeight: active ? typography.weight.semibold : typography.weight.regular,
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {showCustomPicker && (
        <div style={{ marginTop: spacing.sm }}>
          <DatePicker
            value={state.dueDate}
            onChange={(v) =>
              onChange({ dueDate: v, paymentTermsDaysOverride: null })
            }
            minDate={state.issueDate}
            placeholder="Pick a due date"
          />
        </div>
      )}

      {hasClientDefaults && (
        <>
          <ClientDefaultsRow
            label="Deposit %"
            pill={
              depositSource ? (
                <OverridePill
                  source={depositSource}
                  clientName={clientNameLabel}
                  onClick={() => setTooltipFor('deposit_percentage')}
                />
              ) : undefined
            }
          >
            <div style={DEPOSIT_COL}>
              <div style={NUM_INPUT_ROW}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={String(depositPercent)}
                  onChange={(e) => onDepositChange(e.target.value)}
                  placeholder="33"
                  aria-label="Deposit percent"
                  style={NUM_INPUT}
                />
                <span style={{ color: palette.g600, fontSize: typography.size.body }}>%</span>
              </div>
              {state.depositPercentageOverride !== null && (
                <button type="button" onClick={onResetDepositOverride} style={RESET_LINK}>
                  Reset to default
                </button>
              )}
            </div>
          </ClientDefaultsRow>

          <ClientDefaultsRow
            label="Late fees"
            pill={
              lateFeeSource ? (
                <OverridePill
                  source={lateFeeSource}
                  clientName={clientNameLabel}
                  onClick={() => setTooltipFor('late_fee_policy')}
                />
              ) : undefined
            }
          >
            <div style={LATE_FEE_COL}>
              <PolymorphicLateFeeEditor
                policy={
                  state.lateFeePolicyOverride !== null
                    ? state.lateFeePolicyOverride
                    : lateFee.policy
                }
                amount={
                  state.lateFeePolicyOverride !== null
                    ? state.lateFeeAmountOverride
                    : lateFee.amount
                }
                onChange={onLateFeeChange}
              />
              {(state.lateFeePolicyOverride !== null || state.lateFeeAmountOverride !== null) && (
                <button type="button" onClick={onResetLateFeeOverride} style={RESET_LINK}>
                  Reset to default
                </button>
              )}
            </div>
          </ClientDefaultsRow>
        </>
      )}

      <ClientDefaultsRow
        label="Tax rate"
        pill={
          taxRateSource ? (
            <OverridePill
              source={taxRateSource}
              clientName={clientNameLabel}
              onClick={() => setTooltipFor('tax_rate')}
            />
          ) : undefined
        }
      >
        <div style={TAX_INPUT_ROW}>
          <input
            type="text"
            inputMode="decimal"
            value={String(taxRatePercent)}
            onChange={(e) => onTaxRateChange(e.target.value)}
            aria-label="Tax rate percent"
            style={TAX_INPUT}
          />
          <span style={{ color: palette.g600, fontSize: typography.size.body }}>%</span>
        </div>
      </ClientDefaultsRow>

      <div style={{ ...FIELD_LABEL, marginTop: spacing.md }}>Notes (optional)</div>
      <textarea
        value={state.notes}
        onChange={(e) => onChange({ notes: e.target.value })}
        placeholder="Any details for the customer — payment instructions, thanks, scope notes."
        style={NOTES}
      />

      <OverridePillTooltip
        visible={tooltipFor !== null}
        reasoning={tooltipFor ? lookupReasoning(tooltipFor) : ''}
        onDismiss={() => setTooltipFor(null)}
      />
    </section>
  );
}

const CARD: React.CSSProperties = {
  background: palette.white,
  border: `1px solid ${palette.g100}`,
  borderRadius: radii.md,
  padding: spacing.md,
  marginBottom: spacing.md,
};

const HEADER_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: spacing.md,
};

const STEP_LABEL: React.CSSProperties = {
  fontSize: typography.size.micro,
  fontWeight: typography.weight.semibold,
  color: palette.g500,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const SECTION_TITLE: React.CSSProperties = {
  fontSize: typography.size.heading,
  fontWeight: typography.weight.semibold,
  color: palette.g800,
  marginTop: 2,
};

const FIELD_LABEL: React.CSSProperties = {
  fontSize: typography.size.caption,
  fontWeight: typography.weight.medium,
  color: palette.g600,
  marginBottom: spacing.xs,
};

const PILL_ROW: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: spacing.xs,
};

const PILL: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: radii.full,
  border: '1px solid transparent',
  fontSize: typography.size.caption,
  fontFamily: typography.fontFamily,
  cursor: 'pointer',
};

const RESET_LINK: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: palette.accent,
  fontSize: typography.size.caption,
  fontWeight: typography.weight.semibold,
  fontFamily: typography.fontFamily,
  cursor: 'pointer',
  padding: 0,
};

const DEPOSIT_COL: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: spacing.xs,
};

const LATE_FEE_COL: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: spacing.sm,
};

const NUM_INPUT_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: spacing.xs,
  background: palette.white,
  border: `1px solid ${palette.g200}`,
  borderRadius: radii.sm,
  paddingInline: spacing.md,
  alignSelf: 'flex-start',
  minWidth: 110,
};

const NUM_INPUT: React.CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: typography.size.body,
  fontFamily: typography.fontFamily,
  color: palette.g800,
  paddingBlock: spacing.sm,
  width: 60,
  textAlign: 'right',
};

const TAX_INPUT_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: spacing.xs,
  background: palette.white,
  border: `1px solid ${palette.g200}`,
  borderRadius: radii.sm,
  paddingInline: spacing.md,
  alignSelf: 'flex-start',
  minWidth: 110,
};

const TAX_INPUT: React.CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: typography.size.body,
  fontFamily: typography.fontFamily,
  color: palette.g800,
  paddingBlock: spacing.sm,
  width: 60,
  textAlign: 'right',
};

const NOTES: React.CSSProperties = {
  width: '100%',
  minHeight: 80,
  padding: spacing.sm,
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontSize: typography.size.body,
  color: palette.g800,
  fontFamily: typography.fontFamily,
  outline: 'none',
  resize: 'vertical',
  boxSizing: 'border-box',
};
